/**
 * Hook: useReportUser
 *
 * Holds all state and executes use cases for the ReportUserSheet flow.
 *
 * Each entry in `levels` is either a populated array or `null` (loading placeholder).
 * When a non-submit item is tapped, a null placeholder is pushed immediately so the
 * component can navigate to the next step before the fetch completes — the loading
 * indicator shows on the new step rather than blocking navigation.
 *
 * `activeFetchId` guards against stale results if the user navigates back mid-fetch.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import type { UserReportItemEntity } from '@/domain/entities/report/userReportItemEntity';
import { getUserReportItemsUseCase } from '@/domain/usecases/report/getUserReportItemsUseCase';
import { submitUserReportUseCase } from '@/domain/usecases/report/submitUserReportUseCase';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { appLogger } from '@/domain/services/analytics/appLogger';

export interface UseReportUserProps {
  user: UserEntity;
  onFinish: () => void;
}

export interface UseReportUserResult {
  // null entries are loading placeholders for that drill-down level
  levels: (UserReportItemEntity[] | null)[];
  submitItem: UserReportItemEntity | null;
  comment: string;
  isSubmitting: boolean;
  isLastStep: boolean;
  // Navigation signal — component watches this to call stepperRef.current?.goNext()
  pendingNavigation: boolean;
  clearPendingNavigation: () => void;
  // Actions
  setComment: (value: string) => void;
  handleItemTap: (item: UserReportItemEntity) => Promise<void>;
  handleBack: () => void;
  trimLastLevel: () => void;
  handleSubmit: () => Promise<void>;
}

export function useReportUser({ user, onFinish }: UseReportUserProps): UseReportUserResult {
  const { l10n } = useLocale();

  // Start with a null placeholder so step 0 shows loading while root items are fetched
  const [levels, setLevels] = useState<(UserReportItemEntity[] | null)[]>([null]);
  const [submitItem, setSubmitItem] = useState<UserReportItemEntity | null>(null);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(false);

  // Incremented on every new fetch and on back navigation to discard stale results
  const activeFetchId = useRef(0);

  // Fetch root-level items on mount — fills the initial null placeholder.
  // fetchId guards against stale state updates: if the sheet unmounts before
  // the fetch resolves, activeFetchId.current will have been incremented
  // (e.g. by handleBack), so the fetchId check short-circuits both the
  // success and error paths — matching the same guard used in handleItemTap.
  useEffect(() => {
    const fetchRootItems = async () => {
      const fetchId = ++activeFetchId.current;
      try {
        const items = await getUserReportItemsUseCase.execute(null);
        if (fetchId !== activeFetchId.current) return; // sheet unmounted, discard
        setLevels([items]);
      } catch (err) {
        if (fetchId !== activeFetchId.current) return; // sheet unmounted, discard
        appLogger.error('Failed to fetch root items', 'useReportUser.fetchRootItems', { error: err as Error });
        setLevels([[]]);
      }
    };

    fetchRootItems();
  }, []);

  const handleItemTap = useCallback(async (item: UserReportItemEntity) => {
    if (item.isSubmit) {
      setSubmitItem(item);
      setPendingNavigation(true);
    } else {
      // Push null placeholder and navigate immediately — fetch runs while loading shows
      const fetchId = ++activeFetchId.current;
      setLevels(prev => [...prev, null]);
      setPendingNavigation(true);

      try {
        const children = await getUserReportItemsUseCase.execute(item.id);
        if (fetchId !== activeFetchId.current) return; // user navigated back, discard
        setLevels(prev => {
          if (prev[prev.length - 1] !== null) return prev; // safety: slot already replaced
          const updated = [...prev];
          updated[updated.length - 1] = children;
          return updated;
        });
      } catch (err) {
        if (fetchId !== activeFetchId.current) return;
        appLogger.error('Failed to fetch child items', 'useReportUser.handleItemTap', { error: err as Error });
        setLevels(prev => {
          if (prev[prev.length - 1] !== null) return prev;
          const updated = [...prev];
          updated[updated.length - 1] = [];
          return updated;
        });
      }
    }
  }, []);

  // Immediate back action: cancels any in-flight fetch and clears the submit item if
  // we're on the submit step. Caller is responsible for stepperRef.current?.goPrevious().
  const handleBack = () => {
    activeFetchId.current++;
    if (submitItem !== null) {
      setSubmitItem(null);
    }
  };

  // Removes the last drill-down level. Kept separate from handleBack so the component
  // can defer this call until after the slide animation completes — trimming levels
  // while the slide is in flight shifts submit into the viewport mid-transition.
  const trimLastLevel = () => {
    setLevels(prev => prev.slice(0, -1));
  };

  const handleSubmit = useCallback(async () => {
    if (!submitItem) return;

    setIsSubmitting(true);
    try {
      const success = await submitUserReportUseCase.execute(
        submitItem.id,
        user.firebaseId,
        comment
      );
      if (success === true) {
        toast.success(l10n.reportUser.submitSuccess);
        setIsSubmitting(false);
        onFinish();
      } else {
        toast.error(l10n.reportUser.submitError);
        setIsSubmitting(false);
      }
    } catch (err) {
      appLogger.error('Error submitting report', 'useReportUser.handleSubmit', { error: err as Error });
      toast.error(l10n.reportUser.submitError);
      setIsSubmitting(false);
    }
  }, [submitItem, onFinish, comment, user, l10n]);

  return {
    levels,
    submitItem,
    comment,
    isSubmitting,
    isLastStep: submitItem !== null,
    pendingNavigation,
    clearPendingNavigation: () => setPendingNavigation(false),
    setComment,
    handleItemTap,
    handleBack,
    trimLastLevel,
    handleSubmit,
  };
}
